import React, {useEffect, useMemo, useState} from 'react';
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    createSimilarityEdge,
    deleteSimilarityEdge,
    listIcrs,
    resolveIcrNumber,
} from '../../../firebase/firestore/icr';
import {listEeis, searchFintelGaps} from '../../../firebase/firestore/eei';
import {TYPE_COLORS} from './similarityLabels';
import {suggestedEdgeForTarget, targetKey, useBlockedTargetKeys} from './similarityTargets';

function candidateFromIcr(icr) {
    const number = resolveIcrNumber(icr) || icr?.id || '';
    const question = icr?.question || icr?.informationRequirement || icr?.title || '';
    return {
        type: 'ICR',
        id: icr?.id,
        primary: question || number || 'ICR',
        secondary: number,
        createdAt: icr?.createdAt || '',
        search: `${number} ${question}`.toLowerCase(),
    };
}

function candidateFromEei(eei) {
    const number = eei?.eeiNumber || eei?.number || '';
    const question = eei?.text || eei?.title || '';
    return {
        type: 'EEI',
        id: eei?.id,
        primary: question || number || 'EEI',
        secondary: number,
        createdAt: eei?.createdAt || '',
        search: `${number} ${question}`.toLowerCase(),
    };
}

function candidateFromGap(gap) {
    const gapId = gap?.gapNumber || gap?.id || '';
    const question = gap?.text || gap?.gapText || gap?.title || 'Gap';
    const fintel = gap?.reportTitle || '';
    return {
        type: 'GAP',
        id: gap?.id,
        primary: question,
        secondary: [gapId, fintel].filter(Boolean).join(' • '),
        createdAt: gap?.createdAt || '',
        search: `${gapId} ${question} ${fintel}`.toLowerCase(),
    };
}

function createdAtMs(value) {
    if (!value) return 0;
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : 0;
}

function selectedSearchLabel(candidate) {
    if (!candidate) return '';
    const parts = [candidate.secondary, candidate.primary].filter(Boolean);
    return parts.join(' — ') || candidate.primary || '';
}

/**
 * Add Relation dialog for ICR/EEI similarity lists: Similarity (FULL/PARTIAL),
 * combined Gaps/EEIs/ICRs search, optional comment.
 */
export default function AddRelationDialog({
    open,
    subject,
    existingEdges = [],
    onClose,
    onCreated,
}) {
    const [query, setQuery] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [listOpen, setListOpen] = useState(true);
    const [relation, setRelation] = useState('FULL');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const blocked = useBlockedTargetKeys(existingEdges);
    const searchValue = selected ? selectedSearchLabel(selected) : query;

    useEffect(() => {
        if (!open || !listOpen) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const timer = setTimeout(async () => {
            try {
                const q = query.trim().toLowerCase();
                const [icrs, eeisData, gapsData] = await Promise.all([
                    listIcrs(500),
                    listEeis({q: query.trim() || undefined, status: 'Active', page: 0, size: 20}),
                    searchFintelGaps({q: query.trim(), limit: 20}),
                ]);
                let list = [
                    ...(icrs || []).map(candidateFromIcr),
                    ...(eeisData?.items || eeisData?.content || eeisData?.eeis || []).map(candidateFromEei),
                    ...(gapsData?.gaps || []).map(candidateFromGap),
                ];
                list = list.filter((c) => {
                    if (!c.id) return false;
                    if (c.type === subject?.type && String(c.id) === String(subject?.id)) return false;
                    if (blocked.has(targetKey(c.type, c.id))) return false;
                    if (q && !c.search.includes(q)) return false;
                    return true;
                });
                list.sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
                list = list.slice(0, 40);
                if (!cancelled) setCandidates(list);
            } catch (e) {
                if (!cancelled) {
                    setCandidates([]);
                    setError(e?.message || 'Search failed');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, query ? 300 : 0);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, listOpen, query, subject, blocked]);

    useEffect(() => {
        if (!open) {
            setQuery('');
            setSelected(null);
            setListOpen(true);
            setRelation('FULL');
            setComment('');
            setError(null);
        }
    }, [open]);

    const selectCandidate = (candidate) => {
        setSelected(candidate);
        setQuery('');
        setListOpen(false);
    };

    const onSearchChange = (event) => {
        if (selected) setSelected(null);
        setQuery(event.target.value);
        setListOpen(true);
    };

    const canSubmit = useMemo(
        () => Boolean(subject?.type && subject?.id && selected?.id && relation && !saving),
        [subject, selected, relation, saving],
    );

    const submit = async () => {
        if (!canSubmit) return;
        setSaving(true);
        setError(null);
        try {
            // Backend conflicts on non-rejected pairs; remove suggested machine edge first.
            const suggestion = suggestedEdgeForTarget(existingEdges, selected.type, selected.id);
            if (suggestion?.id) {
                await deleteSimilarityEdge(suggestion.id);
            }
            const body = {
                fromType: subject.type,
                fromId: subject.id,
                toType: selected.type,
                toId: selected.id,
                relation,
            };
            const trimmed = comment.trim();
            if (trimmed) body.comment = trimmed;
            await createSimilarityEdge(body);
            if (onCreated) await onCreated();
            if (onClose) onClose();
        } catch (e) {
            setError(e?.message || 'Failed to create relation');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Relation</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="add-relation-similarity-label">Similarity</InputLabel>
                        <Select
                            labelId="add-relation-similarity-label"
                            label="Similarity"
                            value={relation}
                            onChange={(e) => setRelation(e.target.value)}
                            inputProps={{'aria-label': 'Similarity'}}
                        >
                            <MenuItem value="FULL">FULL</MenuItem>
                            <MenuItem value="PARTIAL">PARTIAL</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        fullWidth
                        label="Search"
                        placeholder="Search Gaps, EEIs, and ICRs…"
                        value={searchValue}
                        onChange={onSearchChange}
                        onFocus={() => setListOpen(true)}
                        onClick={() => setListOpen(true)}
                        inputProps={{'aria-label': 'Search'}}
                    />

                    {error && (
                        <Typography variant="caption" color="error">{error}</Typography>
                    )}

                    {listOpen && (loading ? (
                        <Stack alignItems="center" sx={{py: 2}}>
                            <CircularProgress size={20}/>
                        </Stack>
                    ) : (
                        <List
                            dense
                            sx={{
                                maxHeight: 260,
                                overflow: 'auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                            }}
                        >
                            {candidates.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 1.5}}>
                                    No matches.
                                </Typography>
                            )}
                            {candidates.map((c) => (
                                <ListItemButton
                                    key={`${c.type}:${c.id}`}
                                    selected={selected?.id === c.id && selected?.type === c.type}
                                    onClick={() => selectCandidate(c)}
                                    sx={{alignItems: 'flex-start'}}
                                >
                                    <Chip
                                        label={c.type}
                                        size="small"
                                        sx={{
                                            mr: 1,
                                            mt: 0.25,
                                            width: 52,
                                            flexShrink: 0,
                                            bgcolor: `${TYPE_COLORS[c.type] || '#757575'}22`,
                                            color: TYPE_COLORS[c.type] || 'text.secondary',
                                            fontWeight: 600,
                                        }}
                                    />
                                    <ListItemText
                                        primary={c.primary}
                                        secondary={c.secondary || undefined}
                                        sx={{my: 0, minWidth: 0}}
                                        primaryTypographyProps={{variant: 'body2'}}
                                        secondaryTypographyProps={{variant: 'caption', noWrap: true}}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    ))}

                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        label="Comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Optional note about this relation"
                        inputProps={{'aria-label': 'Comment'}}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" sx={{textTransform: 'none'}}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={submit}
                    sx={{textTransform: 'none'}}
                >
                    Add Relation
                </Button>
            </DialogActions>
        </Dialog>
    );
}
